import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { DataTag } from '@/components/DataTag/DataTag'
import { FilterMenu, type FilterOption } from '@/components/FilterMenu'
import SkeletonTable from '@/components/SkeletonTable'
import TableFooter from '@/components/TableFooter/TableFooter'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { routeToAccountManagementWithPrefilledEmail } from '@/features/AccountManagement/pages/AccountManagement'
import { hivesOverviewClusterDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewClusterDetails'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import {
  type FlattenedHivesResponse,
  flattenHivesResponse,
  getColorSchemeForStatus,
} from '@/features/HivesOverview/util'
import { exportHivesToCsv } from '@/features/HivesOverview/util/exportHivesToCsv'
import { useFindHives } from '@/generated/http-clients/v2ApiComponents'
import { FIRST_PAGE } from '@/helpers/constants'
import { timeAgo } from '@/helpers/timeFormatter'
import { useFuseSearch } from '@/helpers/useFuseSearch'
import { toaster } from '@/hooks/useToast/toastUtils'
import { hivesOverviewRootRoute } from '@/router/rootRoutes'
import {
  Badge,
  Box,
  Button,
  Card,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  Portal,
  Table,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute, useNavigate } from '@tanstack/react-router'
import type { IFuseOptions } from 'fuse.js'
import { Download, RefreshCw, Search, WholeWord } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const EXACT_MATCH_STORAGE_KEY = 'hivesOverview.exactMatch'

/**
 * Names the toggle for screen readers. "Exact match" over "whole word": the
 * search matches within a field value, not a word inside one, and operators
 * already know the term from other tools.
 */
const EXACT_MATCH_LABEL = 'Exact match'

/**
 * The icon alone cannot say what the mode does, so the tooltip spells out
 * both sides of the toggle — what is on now, and what turning it off buys.
 */
const EXACT_MATCH_TOOLTIP = {
  on: 'Exact match is on. Results must contain exactly what you type — a typo finds nothing. Click to allow near matches.',
  off: 'Exact match is off. Near matches count, so a typo or partial word still finds results. Click to require an exact match.',
} as const

/** Same state, said briefly, for the line under the field. */
const EXACT_MATCH_HINT = {
  on: 'Exact match on — typos will not match',
  off: 'Near matches allowed — typos still find results',
} as const
const DEFAULT_PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const PLAN_FILTER_OPTIONS: FilterOption[] = [
  { value: 'Starter', label: 'Starter' },
  { value: 'Dedicated', label: 'Dedicated' },
]

const HIVES_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes cache time
  refetchOnWindowFocus: false,
  refetchOnMount: false,
} as const

const FUSE_SEARCH_KEYS: IFuseOptions<FlattenedHivesResponse[number]>['keys'] = [
  { name: 'cluster_id', weight: 1 },
  { name: 'cluster_name', weight: 1 },
  { name: 'org_id', weight: 1 },
  { name: 'company_name', weight: 1 },
  { name: 'apiary', weight: 0.8 },
  { name: 'emails.email_address', weight: 1 },
]

const FUSE_SEARCH_OPTIONS: IFuseOptions<FlattenedHivesResponse[number]> = {
  keys: FUSE_SEARCH_KEYS,
  threshold: 0.25,
  ignoreLocation: true,
  findAllMatches: true,
}

const FUSE_EXACT_SEARCH_OPTIONS: IFuseOptions<FlattenedHivesResponse[number]> =
  {
    keys: FUSE_SEARCH_KEYS,
    threshold: 0,
    ignoreLocation: true,
    findAllMatches: true,
  }

export const hivesOverviewRoute = createRoute({
  getParentRoute: () => hivesOverviewRootRoute,
  path: '/',
  component: HivesOverviewsTable,
})

// NOTE: Exported only for testing purposes, DO NOT import this component directly.
export function HivesOverviewsTable() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [filter, setFilter] = useState('')
  const [exactMatch, setExactMatch] = useState(
    () => localStorage.getItem(EXACT_MATCH_STORAGE_KEY) === 'true',
  )

  useEffect(() => {
    localStorage.setItem(EXACT_MATCH_STORAGE_KEY, String(exactMatch))
  }, [exactMatch])
  const [planFilter, setPlanFilter] = useState<FilterOption | null>(null)
  const hivesRequest = useFindHives({}, HIVES_QUERY_OPTIONS)
  const navigate = useNavigate()

  const { t } = useTranslation(undefined, { useSuspense: false })

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value)
    setPage(FIRST_PAGE)
  }

  const handlePlanFilterChange = useCallback((option: FilterOption) => {
    setPlanFilter((prev) => (prev?.value === option.value ? null : option))
    setPage(FIRST_PAGE)
  }, [])

  const routeToClusterDetails = useCallback(
    (orgId: string, clusterId: string | undefined) => {
      if (clusterId) {
        navigate({
          to: hivesOverviewClusterDetailsRoute.to,
          params: {
            orgId,
            clusterId,
          },
        })
      } else {
        toaster.create({
          title: 'Cluster currently not accessible.',
          type: 'warning',
        })
      }
    },
    [navigate],
  )

  const routeToOrganizationDetails = useCallback(
    (orgId: string) => {
      navigate({
        to: hivesOverviewOrganizationDetailsRoute.to,
        params: {
          orgId,
        },
      })
    },
    [navigate],
  )

  const hivesOverviewTableData = useMemo(() => {
    return flattenHivesResponse(hivesRequest.data?.hives)
  }, [hivesRequest.data?.hives])

  const fuseOptions = exactMatch
    ? FUSE_EXACT_SEARCH_OPTIONS
    : FUSE_SEARCH_OPTIONS
  const searchedHives = useFuseSearch(
    hivesOverviewTableData,
    filter,
    fuseOptions,
  )

  const filteredHives = useMemo(
    () =>
      planFilter
        ? searchedHives.filter((hive) => hive.plan === planFilter.value)
        : searchedHives,
    [searchedHives, planFilter],
  )

  const totalPages = Math.max(1, Math.ceil(filteredHives.length / pageSize))
  const paginatedHives = filteredHives.slice(
    (page - 1) * pageSize,
    page * pageSize,
  )

  function handleNextPage() {
    if (page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  function handlePreviousPage() {
    if (page > FIRST_PAGE) {
      setPage((prev) => prev - 1)
    }
  }

  function goToFirstPage() {
    setPage(FIRST_PAGE)
  }

  function goToLastPage() {
    setPage(totalPages)
  }

  function handlePageSizeChange(newPageSize: number) {
    setPageSize(newPageSize)
    setPage(FIRST_PAGE)
  }

  const skeletonWidths = [120, 100, 250, 150, 100, 80, 200, 100]

  return (
    <PageLayout title={t('sidebar.runningHives')}>
      <VStack gap={0} w="full">
        <HStack
          mb={6}
          w="full"
          justifyContent="space-between"
          alignItems="center"
        >
          <HStack gap={3} flex="1">
            {/* Every control in this row is `sm` so their heights line up, and
              the search box is capped so it stops dwarfing the filters. */}
            {/* Relative wrapper so the hint can hang under the field without
                adding height to the row and nudging the other controls. */}
            <Box position="relative" flex={1} maxWidth="420px">
              <InputGroup
                width="100%"
                startElement={<Icon as={Search} size="sm" />}
                startElementProps={{ pointerEvents: 'none' }}
                // The search modifier belongs to the field it modifies, so it
                // rides inside the input the way editors put their match
                // toggles there, rather than floating beside it as a labelled
                // checkbox. `InputGroup` reserves the padding for it.
                endElement={
                  <Tooltip.Root openDelay={200}>
                    <Tooltip.Trigger asChild>
                      <IconButton
                        aria-label={EXACT_MATCH_LABEL}
                        aria-pressed={exactMatch}
                        size="sm"
                        variant={exactMatch ? 'subtle' : 'ghost'}
                        colorPalette={exactMatch ? 'brand' : 'gray'}
                        p={1}
                        onClick={() => {
                          setExactMatch((wasExact) => !wasExact)
                          setPage(FIRST_PAGE)
                        }}
                        data-testid="exact-match-checkbox"
                      >
                        <Icon as={WholeWord} />
                      </IconButton>
                    </Tooltip.Trigger>
                    {/* Portalled, or the table card below clips it — the
                      tooltip opens downward into that stacking context. */}
                    <Portal>
                      <Tooltip.Positioner>
                        <Tooltip.Content maxW="260px">
                          <Tooltip.Arrow />
                          {exactMatch
                            ? EXACT_MATCH_TOOLTIP.on
                            : EXACT_MATCH_TOOLTIP.off}
                        </Tooltip.Content>
                      </Tooltip.Positioner>
                    </Portal>
                  </Tooltip.Root>
                }
                endElementProps={{ px: 0 }}
              >
                <Input
                  size="sm"
                  placeholder="Search organizations, clusters or emails..."
                  value={filter}
                  onChange={handleFilterChange}
                  backgroundColor="bg"
                />
              </InputGroup>
              {exactMatch && (
                <Text
                  position="absolute"
                  top="100%"
                  right={0}
                  mt={1}
                  fontSize="xs"
                  color="fg.warning"
                  whiteSpace="nowrap"
                  data-testid="search-mode-hint"
                >
                  {EXACT_MATCH_HINT.on}
                </Text>
              )}
            </Box>
            <FilterMenu
              buttonLabel={planFilter ? `Plan: ${planFilter.label}` : 'Plan'}
              options={PLAN_FILTER_OPTIONS}
              selectedOption={planFilter}
              onOptionChange={handlePlanFilterChange}
              highlightWhenSelected
              data-testid="plan-filter"
            />
          </HStack>
          <HStack>
            <HStack gap={2}>
              {hivesRequest.dataUpdatedAt > 0 && (
                <Text fontSize="xs" color="fg.muted" data-testid="last-updated">
                  Last updated {timeAgo(hivesRequest.dataUpdatedAt)}
                </Text>
              )}
              <IconButton
                aria-label="Refresh hives"
                variant="ghost"
                size="sm"
                onClick={() => hivesRequest.refetch()}
                loading={hivesRequest.isFetching}
                data-testid="refresh-hives"
              >
                <Icon as={RefreshCw} />
              </IconButton>
            </HStack>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => exportHivesToCsv(filteredHives)}
              disabled={!hivesRequest.isSuccess || filteredHives.length === 0}
              data-testid="export-csv"
            >
              <Icon as={Download} />
              Export CSV
            </Button>
          </HStack>
        </HStack>
        <Card.Root w="100%" p={4}>
          <Table.Root size="sm" variant="line" interactive>
            <Table.Header>
              <Table.Row>
                {/* Names lead, ids follow: the names are what operators
                  recognise, and they now carry the links. */}
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Hive Name</Table.ColumnHeader>
                <Table.ColumnHeader>Company Name</Table.ColumnHeader>
                <Table.ColumnHeader>Org ID</Table.ColumnHeader>
                <Table.ColumnHeader>Hive ID</Table.ColumnHeader>
                <Table.ColumnHeader>Plan</Table.ColumnHeader>
                <Table.ColumnHeader>Apiary</Table.ColumnHeader>
                <Table.ColumnHeader>Org Members</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {hivesRequest.isLoading && (
                <SkeletonTable skeletonWidths={skeletonWidths} />
              )}

              {hivesRequest.isError && (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <ApiErrorState
                      apiResource={hivesRequest as unknown as UseQueryResult}
                      errorTitle="An error happened"
                      errorDescription="We failed to load the data from the server for the templates."
                    />
                  </Table.Cell>
                </Table.Row>
              )}

              {hivesRequest.isSuccess && filteredHives.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <Text>
                      {hivesOverviewTableData.length === 0
                        ? 'No hives found.'
                        : 'No hives match your search criteria.'}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}

              {hivesRequest.isSuccess &&
                filteredHives.length > 0 &&
                paginatedHives.map((cluster) => (
                  <HiveTableRow
                    key={`${cluster.org_id}-${cluster.cluster_id}`}
                    cluster={cluster}
                    onNavigateToCluster={routeToClusterDetails}
                    onNavigateToOrg={routeToOrganizationDetails}
                  />
                ))}
            </Table.Body>
          </Table.Root>
        </Card.Root>

        {hivesRequest.isSuccess && filteredHives.length > 0 && (
          <TableFooter
            page={page}
            totalPages={totalPages}
            hasNextPage={page < totalPages}
            onFirstPage={goToFirstPage}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onLastPage={goToLastPage}
            onPageChange={setPage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </VStack>
    </PageLayout>
  )
}

const MAX_EMAILS_DISPLAYED = 1

/**
 * Cluster ids are uuids prefixed with a readable segment; only that first
 * segment is worth showing in a dense table. Copying still yields the whole
 * id, so nothing is actually lost.
 */
function shortClusterId(clusterId: string | undefined): string {
  return clusterId ? `${clusterId?.split('-')[0]}...` : 'N/A'
}

const HiveTableRow = React.memo(function HiveTableRow({
  cluster,
  onNavigateToCluster,
  onNavigateToOrg,
}: Readonly<{
  cluster: FlattenedHivesResponse[number]
  onNavigateToCluster: (orgId: string, clusterId: string | undefined) => void
  onNavigateToOrg: (orgId: string) => void
}>) {
  const emailAddresses = useMemo(
    () => cluster.emails.map(({ email_address }) => email_address),
    [cluster.emails],
  )

  return (
    <Table.Row
      cursor="pointer"
      // Whole-row shortcut to the cluster. Clicks that land on a control
      // inside a cell — a copy button, a name link — are that control's, so
      // bail rather than hijacking them.
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('button, a')) {
          return
        }
        onNavigateToCluster(cluster.org_id, cluster.cluster_id)
      }}
    >
      <Table.Cell data-testid="cluster-status">
        <HStack>
          <DataTag
            colorPalette={getColorSchemeForStatus(cluster.hive_status)}
            value={cluster.hive_status}
            hasCopy={false}
            variant="surface"
            fontWeight="medium"
          >
            {cluster.hive_status}
          </DataTag>
        </HStack>
      </Table.Cell>

      <Table.Cell data-testid="cluster-name">
        {/* Unnamed clusters fall back to the shortened id as the link label,
            so the row never loses its way through to the cluster. */}
        <CopyableValue
          value={cluster.cluster_name || cluster.cluster_id}
          onClick={
            cluster.cluster_id
              ? () => onNavigateToCluster(cluster.org_id, cluster.cluster_id)
              : undefined
          }
        >
          {cluster.cluster_name || shortClusterId(cluster.cluster_id)}
        </CopyableValue>
      </Table.Cell>

      {/* The links live on the names — an operator picks a row by company or
          cluster name, not by id. The id columns are read-and-copy only. */}
      <Table.Cell data-testid="company-name">
        <CopyableValue
          value={cluster.company_name}
          onClick={() => onNavigateToOrg(cluster.org_id)}
        />
      </Table.Cell>

      <Table.Cell data-testid="org-id">
        <CopyableValue value={cluster.org_id} />
      </Table.Cell>

      <Table.Cell data-testid="cluster-id">
        {/* Shown trimmed to the first segment — the full uuid is too wide to
            scan and only ever gets copied, which still yields the whole id. */}
        <CopyableValue value={cluster.cluster_id}>
          {shortClusterId(cluster.cluster_id)}
        </CopyableValue>
      </Table.Cell>

      <Table.Cell data-testid="cluster-plan">
        {cluster.plan ? <Badge variant="subtle">{cluster.plan}</Badge> : null}
      </Table.Cell>

      <Table.Cell data-testid="apiary">
        {cluster.apiary ? (
          <Badge variant="subtle">{cluster.apiary}</Badge>
        ) : null}
      </Table.Cell>

      <Table.Cell data-testid="email">
        <OrganizationMembersEmails emails={emailAddresses} />
      </Table.Cell>
    </Table.Row>
  )
})

const OrganizationMembersEmails = React.memo(
  function OrganizationMembersEmails({
    emails,
  }: Readonly<{ emails: (string | undefined)[] }>) {
    const navigate = useNavigate()

    // Drop blanks before capping and counting: an undefined address rendered
    // as nothing but still inflated the "+N more" tally.
    const presentEmails = emails.filter((email): email is string => !!email)
    const cappedEmails = presentEmails.slice(0, MAX_EMAILS_DISPLAYED)

    const { t } = useTranslation('features')

    return (
      <HStack>
        {cappedEmails.map((email) => {
          return (
            <CopyableValue
              key={email}
              value={email}
              onClick={() =>
                routeToAccountManagementWithPrefilledEmail(navigate, email)
              }
            />
          )
        })}
        {presentEmails.length > MAX_EMAILS_DISPLAYED && (
          <Text fontSize="xs" color="gray.500">
            {t('hivesOverview.table.moreEmails', {
              amount: presentEmails.length - MAX_EMAILS_DISPLAYED,
            })}
          </Text>
        )}
      </HStack>
    )
  },
)
